/**
 * Dashboard Page - עמוד לוח בקרה
 * User dashboard with overview
 */

import { useAuthStore } from '../store/authStore'
import { MessageSquare, ClipboardList, TrendingUp, AlertCircle } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function DashboardPage() {
  const { user } = useAuthStore()

  const hasValidQuestionnaire =
    user?.questionnaireExpiresAt &&
    new Date(user.questionnaireExpiresAt) > new Date()

  return (
    <div className="max-w-6xl mx-auto">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">
          שלום, {user?.firstName}!
        </h1>
        <p className="text-gray-600">ברוך הבא לאזור האישי של יפתח ונגר יועץ השקעות</p>
      </div>

      {/* Questionnaire Alert */}
      {!hasValidQuestionnaire && (
        <div className="alert alert-warning mb-6" role="alert">
          <AlertCircle size={20} aria-hidden="true" />
          <div className="flex-1">
            <p className="font-medium">נדרש למלא שאלון סיכון</p>
            <p className="text-sm mt-1">
              לפני שתוכל להשתמש בשירות הצ'אט, יש להשלים את שאלון הסיכון
            </p>
          </div>
          <Link to="/questionnaire" className="btn btn-outline btn-sm">
            מלא שאלון
          </Link>
        </div>
      )}

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Risk Questionnaire Card */}
        <Link
          to="/questionnaire"
          className="card hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary-100 rounded-lg">
              <ClipboardList size={24} className="text-primary-600" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg mb-1">
                שאלון סיכון
              </h2>
              <p className="text-sm text-gray-600">
                {hasValidQuestionnaire
                  ? `ציון סיכון: ${user?.currentRiskScore}/5`
                  : 'השלם את שאלון הסיכון'}
              </p>
            </div>
          </div>
        </Link>

        {/* Chat Card */}
        <Link
          to="/chat"
          className={`card hover:shadow-md transition-shadow ${
            !hasValidQuestionnaire ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={(e) => {
            if (!hasValidQuestionnaire) e.preventDefault()
          }}
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <MessageSquare size={24} className="text-green-600" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg mb-1">
                צ'אט ייעוץ
              </h2>
              <p className="text-sm text-gray-600">
                שוחח עם יועץ ה-AI שלנו
              </p>
            </div>
          </div>
        </Link>

        {/* Recommendations Card */}
        <div className="card">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp size={24} className="text-blue-600" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg mb-1">
                המלצות השקעה
              </h2>
              <p className="text-sm text-gray-600">
                {hasValidQuestionnaire
                  ? 'צפה בהמלצות מותאמות אישית'
                  : 'זמין לאחר שאלון סיכון'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Score Section */}
      {user?.currentRiskScore && (
        <div className="card">
          <h2 className="card-header">פרופיל הסיכון שלך</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  ציון סיכון
                </span>
                <span className="text-2xl font-bold text-primary-600">
                  {user.currentRiskScore}/5
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-primary-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(user.currentRiskScore / 5) * 100}%` }}
                />
              </div>
            </div>

            {user.questionnaireExpiresAt && (
              <div className="text-sm text-gray-600">
                <p>
                  תוקף השאלון עד:{' '}
                  {new Date(user.questionnaireExpiresAt).toLocaleDateString('he-IL')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
